import type { TFunction } from "i18next";
import type { ViewMode } from "@/zapi-frontend/stores/chapter";
import { getExecSummaryLabelKey } from "@/zapi-frontend/shared/lib/projectChapters";
import type { TChapter } from "@/zapi-frontend/shared/types/projects";
import type { TNavTarget, TSectionPageModel, TSectionScreen, TSectionTab } from "../model/types";
import { VALUE_CHAIN_SECTION, type TProjectChapterGroups } from "./groupProjectChapters";

const VALUE_CHAIN_TAB = "view:valueChain";
const CASE_FRAME_TAB = "view:projectFrame";
const PROJECT_SETUP_TAB = "view:projectConfig";

type TSectionPageInput = {
  viewMode: ViewMode | undefined;
  activeChapter: TChapter | null;
  groups: TProjectChapterGroups;
  isTemplateLess: boolean;
  hasValueChain: boolean;
  t: TFunction<"projectHome">;
};

type TSectionScreenInput = {
  hasSectionPage: boolean;
  isContentShown: boolean;
  viewMode: ViewMode | undefined;
  isProjectLoaded: boolean;
  isTemplateLess: boolean;
};

const chapterTabId = (chapter: TChapter) => `chapter:${chapter.id}`;

const chapterLabel = (chapter: TChapter) => chapter.projectTemplateChapter.nav || chapter.projectTemplateChapter.name;

export function parseSectionTabId(tabId: string): TNavTarget {
  if (tabId === VALUE_CHAIN_TAB) return { kind: "view", view: "valueChain" };
  if (tabId === CASE_FRAME_TAB) return { kind: "view", view: "projectFrame" };
  if (tabId === PROJECT_SETUP_TAB) return { kind: "view", view: "projectConfig" };
  return { kind: "chapter", chapterId: Number(tabId.slice("chapter:".length)) };
}

export function deriveSectionPage({
  viewMode,
  activeChapter,
  groups,
  isTemplateLess,
  hasValueChain,
  t
}: TSectionPageInput): TSectionPageModel | null {
  if (viewMode === "projectFrame" || viewMode === "projectConfig") {
    const tabs = [
      { id: CASE_FRAME_TAB, label: t("sidebar.caseFrame") },
      { id: PROJECT_SETUP_TAB, label: t("sidebar.projectSetup") }
    ];
    const active = viewMode === "projectFrame" ? tabs[0] : tabs[1];
    return { title: t("sidebar.frame"), widgetTitle: active.label, tabs, activeTabId: active.id };
  }

  const sectionTabs = (chapters: TChapter[], withValueChain: boolean): TSectionTab[] => [
    ...chapters.map((chapter) => ({ id: chapterTabId(chapter), label: chapterLabel(chapter) })),
    ...(withValueChain ? [{ id: VALUE_CHAIN_TAB, label: t("sidebar.valueChain") }] : [])
  ];

  if (viewMode === "valueChain") {
    const market = groups.sections.find((section) => section.key === VALUE_CHAIN_SECTION);
    if (!market) return null;
    return {
      title: t(market.labelKey),
      widgetTitle: t("sidebar.valueChain"),
      tabs: sectionTabs(market.chapters, true),
      activeTabId: VALUE_CHAIN_TAB
    };
  }

  if (viewMode !== "chapter" || !activeChapter) return null;

  const single = (title: string, widgetTitle: string): TSectionPageModel => ({
    title,
    widgetTitle,
    tabs: [],
    activeTabId: chapterTabId(activeChapter)
  });

  if (activeChapter.id === groups.execChapter?.id) {
    return single(t(getExecSummaryLabelKey(isTemplateLess)), t("sidebar.executiveSummary"));
  }
  if (activeChapter.id === groups.memoChapter?.id) {
    return single(t("sidebar.innoveraMemo"), activeChapter.projectTemplateChapter.name);
  }

  const owner = [
    ...groups.sections.map((section) => ({
      label: t(section.labelKey),
      chapters: section.chapters,
      withValueChain: section.key === VALUE_CHAIN_SECTION && hasValueChain
    })),
    { label: t("sidebar.appendices"), chapters: groups.appendices, withValueChain: false },
    { label: t("sidebar.hiddenChapters"), chapters: groups.hiddenChapters, withValueChain: false }
  ].find((group) => group.chapters.some((chapter) => chapter.id === activeChapter.id));

  if (!owner) return single(activeChapter.projectTemplateChapter.name, activeChapter.projectTemplateChapter.name);

  return {
    title: owner.label,
    widgetTitle: activeChapter.projectTemplateChapter.name,
    tabs: sectionTabs(owner.chapters, owner.withValueChain),
    activeTabId: chapterTabId(activeChapter)
  };
}

export function getSectionScreen({
  hasSectionPage,
  isContentShown,
  viewMode,
  isProjectLoaded,
  isTemplateLess
}: TSectionScreenInput): TSectionScreen | null {
  if (!hasSectionPage || !isContentShown) return null;
  if (viewMode === "chapter") return "chapter";
  if (viewMode === "projectFrame" || viewMode === "projectConfig") return "frame";
  if (viewMode === "valueChain" && isProjectLoaded && !isTemplateLess) return "valueChain";
  return null;
}
